import { Component, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslateParser, TranslateService } from '@ngx-translate/core';
import { User } from 'src/app/models/user';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { ToastService } from 'src/app/services/toast.service';
import { EnableAuthenticatorAppDialogComponent } from './enable-authenticator-app-dialog/enable-authenticator-app-dialog.component';
import { EnableSecurityKeyDialogComponent } from './enable-security-key-dialog/enable-security-key-dialog.component';
import { IdentitySecurityService } from 'src/app/services/identitySecurity';
import { lastValueFrom } from 'rxjs';
import { DisableDialogComponent } from './disable-dialog/disable-dialog.component';
import { PasswordValidator } from 'src/app/directives/password-validator.directive';
import { IsEqualValidator } from 'src/app/directives/is-equal-validator.directive';
import { CannotContainOnlySpaceValidator } from 'src/app/directives/cannot-contain-only-space-validator.directive';
import { BreadCrumbOptions, SettingsBreadCrumbService } from '../../settings-breadcrumb.service';

@Component({
  templateUrl: './account-security.page.html'
})
export class SettingsAccountSecurityPage implements OnInit {

  errorMessage!: string;
  defaultError: boolean = true;
  translations = <any>{};
  private userGuid!: string;
  user: User = new User;
  securityMethods: any;

  form!: UntypedFormGroup;
  orignalPasswordControl: FormControl;
  newPasswordControl: FormControl;
  confirmPasswordControl: FormControl;

  constructor(
    private authenticationService: AuthenticationService,
    private toastService: ToastService,
    private translateParser: TranslateParser,
    private translateService: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private securityService: IdentitySecurityService,
    private modalService: NgbModal,
    private settingsBreadCrumbService: SettingsBreadCrumbService) {
    // set the settings breadcrumb to the correct options:
    this.settingsBreadCrumbService.SetCrumbs(BreadCrumbOptions.Account, BreadCrumbOptions.Account_Security);

    this.authenticationService.currentUser.subscribe((data) => {
      this.userGuid = data.sub;
    });
    this.getTranslations();
    translateService.onLangChange.subscribe(() => {
      this.getTranslations();
    });
    this.orignalPasswordControl = new UntypedFormControl('', [Validators.required, CannotContainOnlySpaceValidator()]);
    this.newPasswordControl = new UntypedFormControl('', [Validators.required, PasswordValidator(), CannotContainOnlySpaceValidator()]);
    this.confirmPasswordControl = new UntypedFormControl('', [Validators.required, PasswordValidator(), CannotContainOnlySpaceValidator()]);
    this.form = this.formBuilder.group({
      oldpassword: this.orignalPasswordControl,
      newpassword: this.newPasswordControl,
      confirmnewpassword: this.confirmPasswordControl
    },
      {
        validator: IsEqualValidator('newpassword', 'confirmnewpassword'),
      });
  }

  getTranslations() {
    this.translateService.get([
      'Toasts.GettingAuthenticationMethodsError',
      'Toasts.DisableTwoFactorError',
      'Toasts.EnableSecurityKeyError',
      'Toasts.DeleteSecurityKeyError',
      'Settings.AccountSecuritySection.Dialog.AuthenticatorApp',
      'Settings.AccountSecuritySection.Dialog.SecurityKey',
      'Toasts.Error',
    ]).subscribe(translation => {
      this.translations.toastsGettingAuthenticationMethodsError = translation['Toasts.GettingAuthenticationMethodsError'];
      this.translations.toastsDisableTwoFactorError = translation['Toasts.DisableTwoFactorError'];
      this.translations.toastsEnableSecurityKeyError = translation['Toasts.EnableSecurityKeyError'];
      this.translations.toastsDeleteSecurityKeyError = translation['Toasts.DeleteSecurityKeyError'];
      this.translations.authenticatorApp = translation['Settings.AccountSecuritySection.Dialog.AuthenticatorApp'];
      this.translations.securityKey = translation['Settings.AccountSecuritySection.Dialog.SecurityKey'];
      this.translations.error = translation['Toasts.Error'];
    });
  }

  ngOnInit(): void {
    this.getAuthenticationMethods();
  }

  private getAuthenticationMethods() {
    this.securityService.getAuthenticationMethods(this.userGuid)
      .then(value => {
        lastValueFrom(value)
          .then((securityMethods: any) => {
            this.securityMethods = securityMethods;
          })
          .catch(() => this.toastService.showError(this.translations.error, this.translations.toastsGettingAuthenticationMethodsError))
      })
      .catch(() => this.toastService.showError(this.translations.error, this.translations.toastsGettingAuthenticationMethodsError));
  }

  translateErrorHandling(item: string) {
    this.translateService.get(["Error.FieldMessage." + item]).subscribe(translation => {
      this.toastService.showError(this.translations.error, "" + this.translateParser.interpolate(translation["Error.FieldMessage." + item]));
      this.defaultError = false;
    });
  }

  authenticatorApp(event: any) {
    if (event.srcElement.checked) this.enableAuthenticatorApp(1);
    else this.disableTwoFactor();
  }

  securityKey(event: any) {
    if (event.srcElement.checked) this.enableSecurityKey(1);
    else this.deleteSecurityKey();
  }

  enableAuthenticatorApp(step: number) {
    let ngbModalOptions: NgbModalOptions = {
      keyboard: false,
      centered: true,
      size: 'sm',
      windowClass: 'dialog-bg-white',
    };
    const modalRef = this.modalService.open(EnableAuthenticatorAppDialogComponent, ngbModalOptions);
    modalRef.componentInstance.step = step;
    modalRef.componentInstance.userId = this.userGuid;
    let checkbox = document.getElementById('authenticatorapp') as HTMLInputElement | null;
    modalRef.dismissed.subscribe({
      next: () => {
        if (modalRef.componentInstance.step < 3) {
          if (checkbox != null) {
            checkbox.checked = false;
          }
        }
      }
    });
    modalRef.closed.subscribe({
      next: () => {
        if (modalRef.componentInstance.step < 3) {
          if (checkbox != null) {
            checkbox.checked = false;
          }
        } else {
          this.toastService.showSaved();
        }
      }
    });
  }

  disableTwoFactor() {
    let ngbModalOptions: NgbModalOptions = {
      keyboard: false,
      centered: true,
      size: 'md',
      windowClass: 'dialog-bg-white',
    };
    const modalRef = this.modalService.open(DisableDialogComponent, ngbModalOptions);
    modalRef.componentInstance.disableType = this.translations.authenticatorApp;
    modalRef.componentInstance.userId = this.userGuid;

    let checkbox = document.getElementById('authenticatorapp') as HTMLInputElement | null;
    modalRef.dismissed.subscribe({
      next: () => {
        if (modalRef.componentInstance.disabled) {
          if (checkbox != null) {
            checkbox.checked = false;
            this.securityService.disableTwoFactor(this.userGuid)
              .then(value =>
                lastValueFrom(value)
                  .then(() => this.getAuthenticationMethods())
                  .catch((error) => this.toastService.showError(this.translations.error, this.translations.toastsDisableTwoFactorError))
              )
              .catch(() => this.toastService.showError(this.translations.error, this.translations.toastsDisableTwoFactorError));
          }
        } else {
          if (checkbox != null) checkbox.checked = true;
        }
      }
    });
    modalRef.closed.subscribe({
      next: () => {
        if (modalRef.componentInstance.disabled) {
          if (checkbox != null) {
            checkbox.checked = false;
            this.securityService.disableTwoFactor(this.userGuid)
              .then(value =>
                lastValueFrom(value)
                  .then(() => {
                    this.getAuthenticationMethods()
                    this.toastService.showSaved();
                  })
                  .catch((error) => this.toastService.showError(this.translations.error, this.translations.toastsDisableTwoFactorError))
              )
              .catch(() => this.toastService.showError(this.translations.error, this.translations.toastsDisableTwoFactorError));
          }
        } else {
          if (checkbox != null) checkbox.checked = true;
        }
      }
    });
  }

  enableSecurityKey(step: number) {
    this.securityService.startSecurityKeyRegistration(this.userGuid)
      .then(value => {
        lastValueFrom(value)
          .then(data => this.openSecurityKeyDialog(step, value))
          .catch((error) => this.toastService.showError(this.translations.error, this.translations.toastsEnableSecurityKeyError))
      })
      .catch((error) => this.toastService.showError(this.translations.error, this.translations.toastsEnableSecurityKeyError));
  }

  deleteSecurityKey() {
    let ngbModalOptions: NgbModalOptions = {
      keyboard: false,
      centered: true,
      size: 'md',
      windowClass: 'dialog-bg-white',
    };
    const modalRef = this.modalService.open(DisableDialogComponent, ngbModalOptions);
    modalRef.componentInstance.disableType = this.translations.securityKey;
    modalRef.componentInstance.userId = this.userGuid;

    let checkbox = document.getElementById('hardwaresecuritykey') as HTMLInputElement | null;
    modalRef.dismissed.subscribe({
      next: () => {
        if (modalRef.componentInstance.disabled) {
          if (checkbox != null) {
            checkbox.checked = false;
            this.securityService.deleteSecurityKey(this.userGuid)
              .then(value =>
                lastValueFrom(value)
                  .then(() => this.getAuthenticationMethods())
                  .catch((error) => this.toastService.showError(this.translations.error, this.translations.toastsDeleteSecurityKeyError))
              )
              .catch(() => this.toastService.showError(this.translations.error, this.translations.toastsDeleteSecurityKeyError));
          }
        } else {
          if (checkbox != null) checkbox.checked = true;
        }
      }
    });
    modalRef.closed.subscribe({
      next: () => {
        if (modalRef.componentInstance.disabled) {
          if (checkbox != null) {
            checkbox.checked = false;
            this.securityService.deleteSecurityKey(this.userGuid)
              .then(value =>
                lastValueFrom(value)
                  .then(() => this.getAuthenticationMethods())
                  .catch((error) => this.toastService.showError(this.translations.error, this.translations.toastsDeleteSecurityKeyError))
              )
              .catch(() => this.toastService.showError(this.translations.error, this.translations.toastsDeleteSecurityKeyError));
          }
        } else {
          if (checkbox != null) checkbox.checked = true;
        }
      }
    });
  }

  private openSecurityKeyDialog(step: number, challenge: any) {
    let ngbModalOptions: NgbModalOptions = {
      keyboard: false,
      centered: true,
      size: 'md',
      windowClass: 'dialog-bg-white'
    };
    const modalRef = this.modalService.open(EnableSecurityKeyDialogComponent, ngbModalOptions);
    modalRef.componentInstance.step = step;
    modalRef.componentInstance.challenge = challenge;
    modalRef.componentInstance.userId = this.userGuid;
    let checkbox = document.getElementById('hardwaresecuritykey') as HTMLInputElement | null;
    modalRef.dismissed.subscribe({
      next: () => {
        if (modalRef.componentInstance.step < 2) {
          if (checkbox != null) {
            checkbox.checked = false;
          }
        }
      }
    });
    modalRef.closed.subscribe({
      next: () => {
        if (modalRef.componentInstance.step < 2) {
          if (checkbox != null) {
            checkbox.checked = false;
          }
        }
      }
    });
  }

}